import { useEffect, useMemo, useRef } from "react";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import type { LatLngBoundsLiteral, Map as LeafletMap } from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapMarker } from "../../MapView.types";
import { buildMarkerNode } from "../../utils/buildMarkerNode";
import { boundsSignature, computeBoundsPoints } from "../../utils/computeBounds";
import { createLeafletIcon } from "../../utils/leafletIcon";
import { MapAdapterProps } from "../MapProviderAdapter.types";
import styles from "./OsmMapAdapter.module.css";

const DEFAULT_TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const FALLBACK_CENTER: [number, number] = [37.7749, -122.4194];
// Close-in zoom used when the camera focuses on a single Stop (see
// `focusMarkerId` below) — tight enough to read as "this one pin", unlike
// the route-wide default `zoom={13}`.
const STOP_FOCUS_ZOOM = 16;

export type OsmMapAdapterProps = MapAdapterProps & { tileUrl?: string };

/** OpenStreetMap / Leaflet implementation of the map adapter contract.
 * Needs no API key, so this is the sandbox's default provider — see
 * MapView.tsx. */
export const OsmMapAdapter = ({ markers, cameraMarkers, focusMarkerId, etaOverlay, tileUrl, className }: OsmMapAdapterProps) => {
  const initialCenter: [number, number] = cameraMarkers[0]
    ? [cameraMarkers[0].position.lat, cameraMarkers[0].position.lng]
    : FALLBACK_CENTER;

  return (
    <MapContainer
      center={initialCenter}
      zoom={13}
      maxZoom={18}
      minZoom={1}
      scrollWheelZoom
      zoomControl
      className={className ?? styles.container}
    >
      <TileLayer url={tileUrl ?? DEFAULT_TILE_URL} attribution="&copy; OpenStreetMap contributors" />
      <BoundsController markers={markers} cameraMarkers={cameraMarkers} focusMarkerId={focusMarkerId} />
      {markers.map((marker) => (
        <MarkerLayer
          key={marker.id}
          marker={marker}
          etaLabel={etaOverlay && etaOverlay.markerId === marker.id ? etaOverlay.label : null}
        />
      ))}
    </MapContainer>
  );
};

/**
 * Camera management: a single effect that always knows exactly one camera
 * target — either the focused Stop, or the all-pins view — and applies it
 * exactly once per target change, never fighting itself:
 *  - No Stop focused: target is the all-pins view. Only recomputed when
 *    *which* markers are on the map changes (initial load, the driver
 *    appearing for the first time), never on a live position update —
 *    `cameraMarkersRef` lets it always read the latest positions without
 *    depending on `cameraMarkers` itself, which is what keeps a driver
 *    location poll from re-triggering this — see `boundsSignature` for why
 *    that's safe.
 *  - A Stop is focused: target is that one marker, at `STOP_FOCUS_ZOOM`.
 * Either way, the very first time this runs for a mounted map, the target is
 * applied directly via `fitBounds`/`setView` (`hasCenteredRef` still false —
 * there's nothing to animate from, it's establishing the initial view);
 * every change after that smoothly transitions the existing map instance to
 * the new target instead, via Leaflet's native `flyToBounds`/`flyTo` — never
 * another plain `fitBounds` call, so returning from a Stop Details page
 * smoothly zooms back out to the all-pins view instead of staying stuck on
 * the last-focused pin, and moving directly between Stops smoothly
 * transitions rather than jumping. `GoogleMapAdapter` mirrors this with
 * Google's own `panTo`/`setZoom` calls (Leaflet, unlike Google Maps, can
 * animate a bounds-fit natively, so no custom zoom math is needed here).
 */
const BoundsController = ({
  markers,
  cameraMarkers,
  focusMarkerId,
}: {
  markers: MapMarker[];
  cameraMarkers: MapMarker[];
  focusMarkerId?: string | null;
}) => {
  const map: LeafletMap = useMap();
  const cameraMarkersRef = useRef(cameraMarkers);
  cameraMarkersRef.current = cameraMarkers;
  const markersRef = useRef(markers);
  markersRef.current = markers;
  const hasCenteredRef = useRef(false);
  const signature = boundsSignature(cameraMarkers);

  useEffect(() => {
    if (focusMarkerId) {
      const focused = markersRef.current.find((marker) => marker.id === focusMarkerId);
      if (!focused) return;
      const target: [number, number] = [focused.position.lat, focused.position.lng];
      if (hasCenteredRef.current) {
        map.flyTo(target, STOP_FOCUS_ZOOM);
      } else {
        map.setView(target, STOP_FOCUS_ZOOM);
      }
      hasCenteredRef.current = true;
      return;
    }

    const points = computeBoundsPoints(cameraMarkersRef.current.map((marker) => marker.position));
    if (points.length === 0) return;
    const bounds: LatLngBoundsLiteral = points.map((point) => [point.lat, point.lng]);
    if (hasCenteredRef.current) {
      map.flyToBounds(bounds, { padding: [56, 56] });
    } else {
      map.fitBounds(bounds, { padding: [56, 56] });
    }
    hasCenteredRef.current = true;
    // Deliberately keyed on `signature`/`focusMarkerId`, not `cameraMarkers`/`markers` — see the doc comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, signature, focusMarkerId]);

  return null;
};

const MarkerLayer = ({ marker, etaLabel }: { marker: MapMarker; etaLabel: string | null }) => {
  const icon = useMemo(() => {
    const { node, size, anchor } = buildMarkerNode(marker, etaLabel);
    return createLeafletIcon(node, size, anchor);
  }, [marker, etaLabel]);

  return <Marker position={[marker.position.lat, marker.position.lng]} icon={icon} />;
};
